import { Pin, PinTheme, PinThemeEntry } from '@lessonup/pins-shared';
import { isString, isUndefined, uniq } from 'lodash';
import { defaultPinTheme } from './defaultPinTheme';
import { isInteractiveQuestionPinType } from './pinTheme.utils';
import { getResetablePinComponentSettings } from './pinThemePinComponentSettingsHandler.utils';
import { getResetablePinSettings } from './pinThemePinSettingsHandler.utils';

export function getPinThemePinSettingsHandlers(theme: PinTheme) {
  return <P extends Pin>(type: P['type'], settings: P['settings']): P['settings'] => {
    const themePinEntryKey = isInteractiveQuestionPinType(type) ? 'interactive' : 'default';

    return computePinSettingsFromPinThemeEntry(settings, theme[themePinEntryKey], defaultPinTheme[themePinEntryKey]);
  };
}

function computePinSettingsFromPinThemeEntry<P extends Pin>(
  settings: P['settings'],
  pinThemeEntry: PinThemeEntry,
  fallbackPinThemeEntry: PinThemeEntry
): P['settings'] {
  const shouldDefaultToThemeBackgroundImage = !settings.backgroundImage && !settings.backgroundColor;
  const themeBackgroundImage = pinThemeEntry.pin.backgroundImage ?? fallbackPinThemeEntry.pin.backgroundImage;
  return {
    ...settings,
    backgroundColor:
      settings.backgroundColor ?? pinThemeEntry.pin.backgroundColor ?? fallbackPinThemeEntry.pin.backgroundColor,
    backgroundImage:
      settings.backgroundImage ?? (shouldDefaultToThemeBackgroundImage ? themeBackgroundImage : undefined),
    backgroundTransparency:
      settings.backgroundTransparency ??
      pinThemeEntry.pin.backgroundTransparency ??
      fallbackPinThemeEntry.pin.backgroundTransparency ??
      100,
  };
}

export function pinThemeSettingsOverrideValidation(pin: Pin) {
  const pinSettingsKeys = getResetablePinSettings(pin);

  if (
    Object.entries(pin.settings).some(
      ([settingKey, settingValue]) => !isUndefined(settingValue) && pinSettingsKeys.includes(settingKey as never)
    )
  ) {
    return true;
  }

  return pin.pinComponents.some((pinComponent) => {
    const pinComponentSettingsKeys = getResetablePinComponentSettings(pinComponent);
    return Object.entries(pinComponent.settings).some(([settingKey, settingValue]) => {
      if (!pinComponentSettingsKeys.includes(settingKey as never)) return false;
      return !isUndefined(settingValue);
    });
  });
}

export function computePinThemeColors(pinTheme: PinTheme): string[] {
  return uniq(
    [...computePinThemeEntryColors(pinTheme.default), ...computePinThemeEntryColors(pinTheme.interactive)].map(
      (color) => color.toUpperCase()
    )
  ).filter((color) => color !== 'TRANSPARENT');
}

function computePinThemeEntryColors(pinThemeEntry: PinThemeEntry): string[] {
  const pinThemeColors = [];

  pinThemeEntry.pin.backgroundColor && pinThemeColors.push(pinThemeEntry.pin.backgroundColor);

  Object.values(pinThemeEntry.pinComponents ?? []).forEach((pinComponentSettings) => {
    Object.entries(pinComponentSettings).forEach(([settingKey, settingValue]) => {
      if (!isString(settingValue)) return;

      if (settingKey.includes('color') || settingKey.includes('Color')) {
        pinThemeColors.push(settingValue);
      }
    });
  });

  return pinThemeColors;
}
